import type {
  ArcturianAvatarState,
  ArcturianOverlayIndicator,
} from './ArcturianAvatarManifest'
import {
  validateAgentOSAvatarManifestV2,
  type AgentOSAvatarAssetV2,
  type AgentOSAvatarManifestV2,
} from './AgentOSAvatarManifestV2.ts'
import type { AgentOSAvatarRole } from './AgentOSAvatarRuntime'

export type ArcturianFullbodyAssetRole = AgentOSAvatarRole

export interface ArcturianFullbodyStateDefinition {
  assets: readonly ArcturianFullbodyAssetRole[]
  fallback_state: 'idle'
  overlay_indicator: ArcturianOverlayIndicator
}

const STORAGE_MEDIA_BASE = 'https://api-storage.arkturian.com/storage/media'
const POSTER_SHA = '70a09657ea2392e2b17199e9f87ca81aa0f1a8874ae713101df18ac51fa1d612'
const POSTER = `${STORAGE_MEDIA_BASE}/117055?width=720&height=1280&format=png&v=${POSTER_SHA}`

function mediaUrl(storageId: number, sha256: string): string {
  return `${STORAGE_MEDIA_BASE}/${storageId}?v=${sha256}`
}

/**
 * Production FULLBODY package. The Web-only renderer profile is deliberate:
 * iOS uses a separate opaque 1:1 variant, while both targets share the same
 * v2 schema and ten-role runtime contract.
 */
const FULLBODY_MANIFEST_V2 = {
  schema: 'agentos.avatar-manifest.v2',
  manifest_revision: '2026-08-02.tschauko-fullbody.v2h.1',
  character_id: 'arcturian.dr-peter-tschauko',
  variant_id: 'fullbody',
  canonical_frame: {
    url: `${STORAGE_MEDIA_BASE}/117054?width=720&height=1280&format=png&v=b2d081ba3772c0fbb8a559c2a371c44c37b017ec06eaafe29d7f35c886703b04`,
    poster: POSTER,
    sha256: 'b2d081ba3772c0fbb8a559c2a371c44c37b017ec06eaafe29d7f35c886703b04',
    width: 720,
    height: 1280,
    aspect_ratio: '9:16',
    opacity_mode: 'chroma_key',
    key_color: '#00B140',
  },
  guarantees: {
    codec_profile: 'h264_high',
    pixel_format: 'yuv420p',
    frame_rate: 24,
    silent: true,
    fast_start: true,
    burned_in_fade: false,
    constant_framing: true,
    loop_diff_mean_max: 2,
    loop_diff_review_max: 64,
  },
  renderer_profiles: {
    web: {
      crossfade_ms: 220,
      easing: 'linear',
      steady_state_decoders: 2,
      transition_decoders: 2,
      supported_frame_rates: [24],
    },
  },
  keying: {
    metric: 'g-max-rb',
    threshold_opaque: 20,
    threshold_transparent: 70,
    despill: 'cap-g-to-max-rb',
  },
  assets: {
    idle: {
      role: 'idle', storage_id: 117143,
      url: mediaUrl(117143, 'acfdd4a691849b70b91f2875d707d4ed2bd64ffff8a6b7de3b73ef9991d3cdaa'),
      poster: POSTER, loop: true, duration: 5.042,
      sha256: 'acfdd4a691849b70b91f2875d707d4ed2bd64ffff8a6b7de3b73ef9991d3cdaa',
      byte_size: 732008, codec_profile: 'h264_high', pixel_format: 'yuv420p',
      width: 720, height: 1280, frame_rate: 24, audio_track_count: 0,
      fast_start: true, burned_in_fade: false, loop_diff_mean: 1.7721, loop_diff_max: 48,
    },
    listening: {
      role: 'listening', storage_id: 117144,
      url: mediaUrl(117144, '6083ab3c6aaaff30ceeb224c85ad2389da42db34f3accdcfe76dc53c4fd16918'),
      poster: POSTER, loop: true, duration: 6.042,
      sha256: '6083ab3c6aaaff30ceeb224c85ad2389da42db34f3accdcfe76dc53c4fd16918',
      byte_size: 727873, codec_profile: 'h264_high', pixel_format: 'yuv420p',
      width: 720, height: 1280, frame_rate: 24, audio_track_count: 0,
      fast_start: true, burned_in_fade: false, loop_diff_mean: 1.5218, loop_diff_max: 50,
    },
    thinking: {
      role: 'thinking', storage_id: 117145,
      url: mediaUrl(117145, '01f5e3b2d3dfa376d113b063f0d7cfd7e87e94981f9dd042e4d137c1ecd53a22'),
      poster: POSTER, loop: true, duration: 6.042,
      sha256: '01f5e3b2d3dfa376d113b063f0d7cfd7e87e94981f9dd042e4d137c1ecd53a22',
      byte_size: 1133173, codec_profile: 'h264_high', pixel_format: 'yuv420p',
      width: 720, height: 1280, frame_rate: 24, audio_track_count: 0,
      fast_start: true, burned_in_fade: false, loop_diff_mean: 1.5972, loop_diff_max: 51,
    },
    talk_short: {
      role: 'talk_short', storage_id: 117146,
      url: mediaUrl(117146, '7a973a6353200227f0ade8584ccdb2eb67370fc47604772de331bff16a0056a2'),
      poster: POSTER, loop: true, duration: 4.042,
      sha256: '7a973a6353200227f0ade8584ccdb2eb67370fc47604772de331bff16a0056a2',
      byte_size: 634544, codec_profile: 'h264_high', pixel_format: 'yuv420p',
      width: 720, height: 1280, frame_rate: 24, audio_track_count: 0,
      fast_start: true, burned_in_fade: false, loop_diff_mean: 1.4308, loop_diff_max: 41,
    },
    talk_medium: {
      role: 'talk_medium', storage_id: 117147,
      url: mediaUrl(117147, 'bd836cc686f27e7fc9ddfd1386ef0146685450e089da323842a9d3bca1ad99e5'),
      poster: POSTER, loop: true, duration: 8.042,
      sha256: 'bd836cc686f27e7fc9ddfd1386ef0146685450e089da323842a9d3bca1ad99e5',
      byte_size: 1733561, codec_profile: 'h264_high', pixel_format: 'yuv420p',
      width: 720, height: 1280, frame_rate: 24, audio_track_count: 0,
      fast_start: true, burned_in_fade: false, loop_diff_mean: 1.7713, loop_diff_max: 56,
    },
    talk_long: {
      role: 'talk_long', storage_id: 117148,
      url: mediaUrl(117148, '156579c732b762ab77a059314b1f725f59ef32c6edfd756095dc89be6e1b2785'),
      poster: POSTER, loop: true, duration: 10.042,
      sha256: '156579c732b762ab77a059314b1f725f59ef32c6edfd756095dc89be6e1b2785',
      byte_size: 2564951, codec_profile: 'h264_high', pixel_format: 'yuv420p',
      width: 720, height: 1280, frame_rate: 24, audio_track_count: 0,
      fast_start: true, burned_in_fade: false, loop_diff_mean: 1.9746, loop_diff_max: 66,
    },
    happy: {
      role: 'happy', storage_id: 117149,
      url: mediaUrl(117149, 'd562642826d57c1e1bb31e62607d698ea00b82d30f19343f4dc21c5b1fd4939f'),
      poster: POSTER, loop: true, duration: 6.042,
      sha256: 'd562642826d57c1e1bb31e62607d698ea00b82d30f19343f4dc21c5b1fd4939f',
      byte_size: 1029374, codec_profile: 'h264_high', pixel_format: 'yuv420p',
      width: 720, height: 1280, frame_rate: 24, audio_track_count: 0,
      fast_start: true, burned_in_fade: false, loop_diff_mean: 1.5827, loop_diff_max: 43,
    },
    joyful: {
      role: 'joyful', storage_id: 117150,
      url: mediaUrl(117150, '967a61c896041f0911b28af1662055daddd052957ab75665de3bb77ebdffa16a'),
      poster: POSTER, loop: true, duration: 6.042,
      sha256: '967a61c896041f0911b28af1662055daddd052957ab75665de3bb77ebdffa16a',
      byte_size: 1555990, codec_profile: 'h264_high', pixel_format: 'yuv420p',
      width: 720, height: 1280, frame_rate: 24, audio_track_count: 0,
      fast_start: true, burned_in_fade: false, loop_diff_mean: 1.6567, loop_diff_max: 53,
    },
    sad: {
      role: 'sad', storage_id: 117151,
      url: mediaUrl(117151, '16380f6cb2b602c46224e5eee42ae05492a2c3bc430867af11582583ead45093'),
      poster: POSTER, loop: true, duration: 6.042,
      sha256: '16380f6cb2b602c46224e5eee42ae05492a2c3bc430867af11582583ead45093',
      byte_size: 930704, codec_profile: 'h264_high', pixel_format: 'yuv420p',
      width: 720, height: 1280, frame_rate: 24, audio_track_count: 0,
      fast_start: true, burned_in_fade: false, loop_diff_mean: 1.6433, loop_diff_max: 63,
    },
    greeting: {
      role: 'greeting', storage_id: 117152,
      url: mediaUrl(117152, '8007439e29967b6f1917d78ef84c962fec6a8c1786dd68282b1951f8f8f7f5d4'),
      poster: POSTER, loop: false, duration: 5.042,
      sha256: '8007439e29967b6f1917d78ef84c962fec6a8c1786dd68282b1951f8f8f7f5d4',
      byte_size: 1111269, codec_profile: 'h264_high', pixel_format: 'yuv420p',
      width: 720, height: 1280, frame_rate: 24, audio_track_count: 0,
      fast_start: true, burned_in_fade: false, loop_diff_mean: 1.583, loop_diff_max: 39,
    },
  },
} as const satisfies AgentOSAvatarManifestV2

// Fail closed at module load. A corrupt or incomplete production package must
// never silently fall back to the unrelated MIDSHOT media.
export const ARCTURIAN_FULLBODY_MANIFEST_VALIDATION = validateAgentOSAvatarManifestV2(
  FULLBODY_MANIFEST_V2,
  'web',
)

export const ARCTURIAN_FULLBODY_MANIFEST = {
  ...FULLBODY_MANIFEST_V2,
  default_variant: false,
  web_runtime: {
    render_width: 540,
    render_height: 960,
  },
  states: {
    idle: { assets: ['idle'], fallback_state: 'idle', overlay_indicator: 'none' },
    listening: { assets: ['listening'], fallback_state: 'idle', overlay_indicator: 'none' },
    thinking: { assets: ['thinking'], fallback_state: 'idle', overlay_indicator: 'none' },
    speaking: {
      assets: ['talk_short', 'talk_medium', 'talk_long'],
      fallback_state: 'idle',
      overlay_indicator: 'none',
    },
    pleased: {
      assets: ['happy', 'joyful'],
      fallback_state: 'idle',
      overlay_indicator: 'none',
    },
    concerned: { assets: ['sad'], fallback_state: 'idle', overlay_indicator: 'none' },
    error: { assets: [], fallback_state: 'idle', overlay_indicator: 'error' },
    greeting: { assets: ['greeting'], fallback_state: 'idle', overlay_indicator: 'none' },
  } satisfies Record<ArcturianAvatarState, ArcturianFullbodyStateDefinition>,
} as const

export function fullbodyAssetForRole(
  role: ArcturianFullbodyAssetRole,
): AgentOSAvatarAssetV2 & { storage_id: number } {
  return ARCTURIAN_FULLBODY_MANIFEST.assets[role]
}

export function fullbodyOverlayForState(state: ArcturianAvatarState): ArcturianOverlayIndicator {
  return ARCTURIAN_FULLBODY_MANIFEST.states[state].overlay_indicator
}
